import _ from 'lodash'
import dayjs from 'dayjs'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { usePatientIdentifierLabel } from '@/hooks/usePatientIdentifierLabel'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'

import ContentPasteIcon from '@mui/icons-material/ContentPaste'
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded'

import TextInput from '@/components/ui/inputs/TextInput'
import InfoSection from '@/components/ui/sections/InfoSection'

import { Consultation } from '@/core/models/consultation.model'

import colors from '@/assets/scss/_themes-vars.module.scss'
import ConsultationLightTable from '@/components/ui/tables/ConsultationLightTable'
import ParentDialog from '@/pages/patient/components/ParentDialog'
import { Accordion, AccordionDetails, AccordionSummary, Button, Dialog, DialogContent, Typography } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Link } from 'react-router-dom'
import { useIsMobile, useIsWebView } from '@/hooks/useSizeBreakpoints'
import { InfoPatientPage } from '@/pages/patient'
import { PatientDocuments as Documents } from '@/features/patient/patient-documents'
import { useIsUS } from '@/hooks/useIsUS'
import { formatBirthday } from '@/utils/dateUtils'
import { useGetActivePregnancyQuery } from '@/core/services/pregnancy'
import Chip from '@mui/material/Chip'

type ParentsSectionProps = {
  parents: Array<{
    firstname: string
    lastname: string
    email?: string
    phone?: string
    city?: string
    cp?: string
  }>
}

type PatientProfileProps = {
  consultation: Consultation
}

function ParentsSection({ parents }: ParentsSectionProps) {
  const { t } = useTranslation()

  const [dialogOpen, setDialogOpen] = useState<boolean>(false)
  const [selectedParentIndex, setSelectedParentIndex] = useState<number | undefined>()

  const toggleDialog = (parentIndex?: number) => () => {
    setSelectedParentIndex(parentIndex)
    setDialogOpen(!dialogOpen)
  }

  return (
    <InfoSection label={t('page.consultation.patientProfile.infoSection.parents.label')}>
      {!parents.length ? (
        <Alert icon={false} severity="info" variant="outlined" sx={{ mt: '16px' }}>
          {t('page.consultation.patientProfile.infoSection.parents.noParentMessage')}
        </Alert>
      ) : (
        <TableContainer component={Paper} sx={{ mt: '16px' }}>
          <Table>
            <TableBody>
              {parents.map((parent, index) => (
                <TableRow key={index}>
                  <TableCell component="th" scope="row">
                    {`${parent.firstname} ${parent.lastname}`}
                  </TableCell>
                  <TableCell align="right">
                    <Stack display="inline-flex" direction="row" spacing={1}>
                      <Tooltip title={t('common:button.show.tooltip')}>
                        <IconButton onClick={toggleDialog(index)} size="small">
                          <VisibilityRoundedIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                  {dialogOpen && selectedParentIndex === index && (
                    <ParentDialog
                      initialValues={{
                        firstname: parent.firstname,
                        lastname: parent.lastname,
                        email: parent.email || '',
                        phone: parent.phone || '',
                        city: parent.city || '',
                        cp: (parent.cp || '') + '',
                      }}
                      mode="update"
                      onClose={toggleDialog()}
                      onSubmit={() => {}}
                      open
                      readOnly
                    />
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </InfoSection>
  )
}

function PregnancyIndicator({ patientId }: { patientId: string }) {
  const { t } = useTranslation()
  const { data: activePregnancy } = useGetActivePregnancyQuery(patientId, { skip: !patientId })

  if (!activePregnancy) return null

  const dueDate = activePregnancy.expectedDueDate ? dayjs(activePregnancy.expectedDueDate).format('DD/MM/YYYY') : null

  return (
    <InfoSection label={t('page.consultation.patientProfile.infoSection.pregnancy.label', { defaultValue: 'Pregnancy' })}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: '10px' }}>
        <Chip label={t('pregnancy.status.active', { defaultValue: 'Active Pregnancy' })} color="success" size="small" />
        {dueDate && (
          <Typography variant="body2" color="text.secondary">
            {t('pregnancy.dueDate', { defaultValue: 'Due: {{date}}', date: dueDate })}
          </Typography>
        )}
      </Stack>
    </InfoSection>
  )
}

export default function PatientProfile(props: PatientProfileProps) {
  const { consultation } = props
  const { t } = useTranslation()
  const identifierLabel = usePatientIdentifierLabel()
  const isMobile = useIsMobile()
  const isWebView = useIsWebView()
  const isUS = useIsUS()
  const [patientDialog, setPatientDialog] = useState(false)

  const { patient } = consultation
  const patientContactInfo = patient?.contactInfo
  const patientUserInfo = patient?.userInfo

  const onPatientDialogButtonClick = () => {
    setPatientDialog(true)
  }

  const consultationLightTableData = useMemo(
    () => [
      {
        id: consultation._id,
        doctor: {
          name: consultation.medecin?.userInfo?.name || '',
          surname: consultation.medecin?.userInfo?.surname || '',
          gender: consultation.medecin?.userInfo?.gender || 'I',
        },
        patient: {
          gender: consultation.patient.userInfo.gender || 'I',
          name: consultation.patient.userInfo.name || '',
          surname: consultation.patient.userInfo.surname || '',
        },
        startDate: consultation.startDate,
        status: consultation.status,
        type: consultation.type,
      },
    ],
    [consultation]
  )

  return (
    <Paper>
      <List component="div" sx={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
        <ListItem sx={{ width: 'auto' }}>
          <ContentPasteIcon fontSize="large" sx={{ mr: '10px' }} />
          <ListItemText
            primary={_.trim(`${patientUserInfo?.surname || ''} ${patientUserInfo?.name || ''}`)}
            secondary={t('page.consultation.tab.patientProfile')}
            sx={{
              '& .MuiListItemText-primary': {
                color: colors.gray900,
                fontSize: '18px',
                fontWeight: 'bold',
              },
              '& .MuiListItemText-secondary': {
                color: colors.indigo700,
                fontSize: '14px',
                fontWeight: 'bold',
              },
            }}
          />
        </ListItem>

        <ListItem sx={{ width: 'auto' }}>
          <ListItemText
            primary={identifierLabel}
            secondary={isUS ? patient?.primaryPolicy : patient?.secu}
            sx={{
              '& .MuiListItemText-primary': {
                color: colors.gray500,
                fontSize: '14px',
                mb: '4px',
              },
              '& .MuiListItemText-secondary': {
                color: colors.gray900,
                fontSize: '14px',
              },
            }}
          />
        </ListItem>
      </List>
      <Box sx={{ padding: '20px 16px' }}>
        {isWebView ? (
          <Button onClick={onPatientDialogButtonClick} variant="contained" color="primary">
            {t('page.patients.seePatientDocumentButton.label')}
          </Button>
        ) : (
          <Button component={Link} to={`/patients/${patient._id}`} target="_blank" variant="contained" color="primary">
            {t('page.patients.seeProfileButton.tooltip')}
          </Button>
        )}
      </Box>
      <Divider />
      <Box sx={{ padding: '20px 16px' }}>
        <Grid container rowSpacing={4} columnSpacing={4}>
          <Grid item xs={12} md={6}>
            <Grid container direction="column" rowSpacing={4} columnSpacing={4}>
              <Grid item xs={12}>
                <InfoSection label={t('page.consultation.patientProfile.infoSection.generalInformation.label')}>
                  <Grid container rowSpacing={4} columnSpacing={isMobile ? 2 : 4} sx={{ mt: '0px' }}>
                    <Grid item xs={6}>
                      <TextInput fullWidth readOnly label={t('field.lastName.label')} value={patientUserInfo?.name} />
                    </Grid>
                    <Grid item xs={6}>
                      <TextInput fullWidth readOnly label={t('field.firstName.label')} value={patientUserInfo?.surname} />
                    </Grid>
                    {isUS && (
                      <Grid item xs={12}>
                        <TextInput fullWidth readOnly label={t('field.insured.label')} value={patient?.insured} />
                      </Grid>
                    )}
                    <Grid item xs={12} md={6}>
                      <TextInput fullWidth readOnly label={t('field.email.label')} value={patient?.email} />
                    </Grid>
                    <Grid item xs={6}>
                      <TextInput fullWidth readOnly label={t('field.gender.label')} value={patientUserInfo?.gender} />
                    </Grid>
                    <Grid item xs={6}>
                      <TextInput fullWidth readOnly label={t('field.birthday.label')} value={formatBirthday(patientUserInfo.birthday ?? '', isUS)} />
                    </Grid>
                    <Grid item xs={6}>
                      <TextInput fullWidth readOnly label={t('field.phone.label')} value={patientContactInfo?.phone} />
                    </Grid>
                    <Grid item xs={6}>
                      {isUS ? (
                        <TextInput fullWidth readOnly label={t('field.insuranceProvider.label')} value={patient?.insuranceProvider} />
                      ) : (
                        <TextInput fullWidth readOnly label={t('field.codeGestion.label')} value={patient?.codeGestion} />
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {isUS ? (
                        <TextInput fullWidth readOnly label={t('field.primaryInsurance.label')} value={patient?.primaryInsurance} />
                      ) : (
                        <TextInput fullWidth readOnly label={t('field.codeRegime.label')} value={patient?.codeRegime} />
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {isUS ? (
                        <TextInput fullWidth readOnly label={t('field.insuredRelationship.label')} value={patient?.insuredRelationship} />
                      ) : (
                        <TextInput fullWidth readOnly label={t('field.caisse.label')} value={patient?.caisse} />
                      )}
                    </Grid>
                    {isUS && (
                      <Grid item xs={12} md={6}>
                        <TextInput fullWidth readOnly label={t('field.secondaryInsurance.label')} value={patient.secondaryInsurance} />
                      </Grid>
                    )}
                    {isMobile ? (
                      <Grid item xs={12}>
                        <Accordion>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="health-info-content" id="health-info-header">
                            <InfoSection label={t('page.consultation.patientProfile.infoSection.healthInformation.label')} />
                          </AccordionSummary>
                          <AccordionDetails>
                            <Grid container rowSpacing={3} columnSpacing={2} sx={{ mt: '0px' }}>
                              <Grid item xs={6}>
                                <TextInput fullWidth readOnly label={t('field.weight.label')} value={patient?.weight} />
                              </Grid>
                              <Grid item xs={6}>
                                <TextInput fullWidth readOnly label={t('field.height.label')} value={patient?.height} />
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <TextInput fullWidth readOnly label={t('field.allergy.label')} value={patient?.allergy} />
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <TextInput fullWidth readOnly label={t('field.vaccin.label')} value={patient?.vaccins} />
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <TextInput fullWidth readOnly label={t('field.background.label')} value={patient?.background} />
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <TextInput fullWidth readOnly label={t('field.treatment.label')} value={patient?.treatment} />
                              </Grid>
                              <Grid item xs={12}>
                                <TextInput fullWidth readOnly label={t('field.notes.label')} value={patient?.notes} />
                              </Grid>
                              {isUS && (
                                <Grid item xs={12}>
                                  <TextInput fullWidth multiline readOnly label={t('field.primaryPharmacy.label')} value={patient?.primaryPharmacy} />
                                </Grid>
                              )}
                            </Grid>
                          </AccordionDetails>
                        </Accordion>
                      </Grid>
                    ) : (
                      <>
                        <Grid item xs={6}>
                          <TextInput fullWidth readOnly label={t('field.weight.label')} value={patient?.weight} />
                        </Grid>
                        <Grid item xs={6}>
                          <TextInput fullWidth readOnly label={t('field.height.label')} value={patient?.height} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextInput fullWidth readOnly label={t('field.allergy.label')} value={patient?.allergy} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextInput fullWidth readOnly label={t('field.vaccin.label')} value={patient?.vaccins} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextInput fullWidth readOnly label={t('field.background.label')} value={patient?.background} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextInput fullWidth readOnly label={t('field.treatment.label')} value={patient?.treatment} />
                        </Grid>
                        <Grid item xs={12}>
                          <TextInput fullWidth readOnly label={t('field.notes.label')} value={patient?.notes} />
                        </Grid>
                        {isUS && (
                          <Grid item xs={12}>
                            <TextInput fullWidth multiline readOnly label={t('field.primaryPharmacy.label')} value={patient?.primaryPharmacy} />
                          </Grid>
                        )}
                      </>
                    )}
                  </Grid>
                </InfoSection>
              </Grid>
              <Grid item xs={12}>
                {isMobile ? (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="address-info-content" id="address-info-header">
                      <InfoSection label={t('page.consultation.patientProfile.infoSection.addressInformation.label')} />
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container rowSpacing={3} columnSpacing={2} sx={{ mt: '0px' }}>
                        <Grid item xs={12} md={6}>
                          <TextInput fullWidth readOnly label={t('field.street.label')} value={patientContactInfo?.street} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextInput fullWidth readOnly label={t('field.city.label')} value={patientContactInfo?.city} />
                        </Grid>
                        <Grid item xs={6}>
                          <TextInput fullWidth readOnly label={t('field.postalCode.label')} value={patientContactInfo?.cp} />
                        </Grid>
                        <Grid item xs={6}>
                          <TextInput fullWidth readOnly label={t('field.country.label')} value={patientContactInfo?.country} />
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                ) : (
                  <InfoSection label={t('page.consultation.patientProfile.infoSection.addressInformation.label')}>
                    <Grid container rowSpacing={4} columnSpacing={4} sx={{ mt: '0px' }}>
                      <Grid item xs={6}>
                        <TextInput fullWidth readOnly label={t('field.street.label')} value={patientContactInfo?.street} />
                      </Grid>
                      <Grid item xs={6}>
                        <TextInput fullWidth readOnly label={t('field.city.label')} value={patientContactInfo?.city} />
                      </Grid>
                      <Grid item xs={6}>
                        <TextInput fullWidth readOnly label={t('field.postalCode.label')} value={patientContactInfo?.cp} />
                      </Grid>
                      <Grid item xs={6}>
                        <TextInput fullWidth readOnly label={t('field.country.label')} value={patientContactInfo?.country} />
                      </Grid>
                    </Grid>
                  </InfoSection>
                )}
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={6}>
            <Grid container direction="column" rowSpacing={4}>
              <Grid item xs={12}>
                {isMobile ? (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="doctor-info-content" id="doctor-info-header">
                      <InfoSection label={t('page.consultation.patientProfile.infoSection.privateDoctor.label')} />
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container rowSpacing={3} columnSpacing={2} sx={{ mt: '0px' }}>
                        <Grid item xs={6}>
                          <TextInput fullWidth label={t('field.firstName.label')} readOnly value={patient?.attendingPhysician?.firstname} />
                        </Grid>
                        <Grid item xs={6}>
                          <TextInput fullWidth label={t('field.lastName.label')} readOnly value={patient?.attendingPhysician?.lastname} />
                        </Grid>
                        <Grid item xs={12}>
                          <TextInput fullWidth label={t('field.email.label')} readOnly value={patient?.attendingPhysician?.email} />
                        </Grid>
                        <Grid item xs={6}>
                          <TextInput fullWidth label={t('field.phone.label')} readOnly value={patient?.attendingPhysician?.phone} />
                        </Grid>
                        <Grid item xs={6}>
                          <TextInput fullWidth label={t('field.postalCode.label')} readOnly value={patient?.attendingPhysician?.cp} />
                        </Grid>
                        <Grid item xs={12}>
                          <TextInput fullWidth label={t('field.city.label')} readOnly value={patient?.attendingPhysician?.city} />
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                ) : (
                  <InfoSection label={t('page.consultation.patientProfile.infoSection.privateDoctor.label')}>
                    <Grid container rowSpacing={4} columnSpacing={4} sx={{ mt: '0px' }}>
                      <Grid item xs={6}>
                        <TextInput fullWidth label={t('field.firstName.label')} readOnly value={patient?.attendingPhysician?.firstname} />
                      </Grid>
                      <Grid item xs={6}>
                        <TextInput fullWidth label={t('field.lastName.label')} readOnly value={patient?.attendingPhysician?.lastname} />
                      </Grid>
                      <Grid item xs={6}>
                        <TextInput fullWidth label={t('field.email.label')} readOnly value={patient?.attendingPhysician?.email} />
                      </Grid>
                      <Grid item xs={6}>
                        <TextInput fullWidth label={t('field.phone.label')} readOnly value={patient?.attendingPhysician?.phone} />
                      </Grid>
                      <Grid item xs={6}>
                        <TextInput fullWidth label={t('field.city.label')} readOnly value={patient?.attendingPhysician?.city} />
                      </Grid>
                      <Grid item xs={6}>
                        <TextInput fullWidth label={t('field.postalCode.label')} readOnly value={patient?.attendingPhysician?.cp} />
                      </Grid>
                    </Grid>
                  </InfoSection>
                )}
              </Grid>
              <Grid item xs={6}>
                <ParentsSection parents={patient.parents} />
              </Grid>
              <Grid item xs={12}>
                <PregnancyIndicator patientId={patient._id} />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <InfoSection label={t('page.consultation.patientProfile.infoSection.pastConsultations.label')}>
              <ConsultationLightTable data={consultationLightTableData} minWidth="unset" />
            </InfoSection>
          </Grid>
        </Grid>
      </Box>
      {isWebView && (
        <Dialog
          onClose={() => setPatientDialog(false)}
          open={patientDialog}
          PaperProps={{
            sx: {
              minWidth: isMobile ? '350px' : '800px',
            },
          }}
        >
          <DialogContent>
            <InfoPatientPage patientId={patient._id} showPastConsultationsTab={false} showInformationTab={false} />
          </DialogContent>
        </Dialog>
      )}
    </Paper>
  )
}
